import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Account } from 'app/back-office/account.model';
import { CustomerModel } from 'app/account-settings/models/customer.model';
import { AccountService } from 'app/account/account.service';
import { CustomersService } from 'app/account/customers.service';
import { Limitations } from 'app/subscription/subscription.model';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { CurrentSiteIdStore } from './current-site-id.store';
import { SubscriptionService } from 'app/subscription/subscription.service';
import { Logger, LoggerFactory } from './logger.service';
import { map, switchMap } from 'rxjs/operators';
import { UserService } from 'app/user/user.service';
import { UserModel } from 'app/account-settings/models/user.model';
import { TrackerService } from 'app/shared/services/tracker.service';
import { SnackBarService } from './snack-bar.service';
import { TierAccess } from 'app/shared/account/tier.model';
import { StorageCurrentContextService } from './storage-current-context.service';

export interface CurrentContext {
  account?: Account;
  customer?: CustomerModel;
  limitations?: Limitations;
  user?: UserModel;
  //identity?: Identity; // TODO: Move here rather than using it's own IdentityService
  accountHasVisits?: boolean;
  tierAccess: TierAccess;
}

@Injectable({ providedIn: 'root' })
export class CurrentContextService {
  private logger: Logger;
  /**
   * Caches the customer/account/limitations bundle across the session and emits the cached
   * value to every new subscriber. A backend mutation made in another tab (e.g. a Shopify
   * install patching `customer.subscriptionProvider`) is not reflected here until
   * `loadCurrentContext(bypassAccountCache: true)` is called. Subscribers whose UI must not
   * flash a stale value (compliance "not even a flash" rule, see TodoBilling.md:16) should
   * trigger a refresh and `skip(1)` the cached emission.
   */
  public currentContextBS = new BehaviorSubject<CurrentContext>(undefined);
  private readonly destroyRef = inject(DestroyRef);
  // Prevents multiple concurrent ping loops.
  private isPingingVisitors = false;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly currentSiteIdStore: CurrentSiteIdStore,
    private readonly accountService: AccountService,
    private readonly customersService: CustomersService,
    private readonly subscriptionService: SubscriptionService,
    private readonly userService: UserService,
    private readonly trackerService: TrackerService,
    private readonly snackbarService: SnackBarService,
    private readonly storageCurrentContextService: StorageCurrentContextService,
  ) {
    this.logger = loggerFactory.get('CurrentContextService');
  }

  // Call tracker every X seconds to know if the site have gotten any visitor.
  // Bounded by MAX_ATTEMPTS so the loop cannot run forever if the user never gets visits.
  private async pingCurrentVisitorsForSite() {
    if (this.isPingingVisitors) {
      return;
    }
    this.isPingingVisitors = true;
    try {
      const MAX_ATTEMPTS = 120; // ~1 hour of polling at 30s interval, then stop.
      let hasVisits = false;
      for (let attempt = 0; attempt < MAX_ATTEMPTS && !hasVisits; attempt++) {
        this.logger.debug('Waiting 30sec to check again for current visitors on site');
        await this.delay(30000); // 30 seconds
        try {
          hasVisits = (await this.trackerService.getCurrentSiteVisitCount().toPromise()) > 0;
        } catch (err) {
          this.logger.warn(`getCurrentSiteVisitCount failed during ping loop: ${err?.message ?? err}`);
          continue;
        }
        this.logger.debug(`Has current visitors on site: ${hasVisits}`);
        if (hasVisits) {
          this.updateCurrentContextHasVisits(true); // Inform all subscriptions about new value
          this.snackbarService.success('settings.account.code.tracking-code-installed');
          break;
        }
      }
    } finally {
      this.isPingingVisitors = false;
    }
  }

  private async delay(ms: number) {
    return await new Promise((resolve) => setTimeout(resolve, ms));
  }

  public loadCurrentContext(bypassAccountCache?: boolean, user?: UserModel): void {
    const currentSiteId = this.currentSiteIdStore.get() ?? 0;
    if (currentSiteId > 0) {
      // First we start request to get the account
      const accountData$ = this.accountService.getAccount(currentSiteId, bypassAccountCache);

      // Then we can in parallel start requests for customer/limitations
      const customerData$: Observable<any> = accountData$.pipe(switchMap((account) => this.getCustomer(account)));
      const limitationsData$: Observable<any> = accountData$.pipe(
        switchMap((account) => this.getLimitations(account)) as any,
      );
      const userData$: Promise<UserModel> = user ? Promise.resolve(user) : this.getUser();
      const accountHasVisits$: Observable<boolean> = accountData$.pipe(
        switchMap(async (account) => {
          if (account.isExpired) {
            return account.hasVisits;
          }
          if (account.hasVisits) {
            return true;
          }
          const siteVisitCount = await this.trackerService.getCurrentSiteVisitCount().toPromise();
          const hasCurrentVisitors = siteVisitCount > 0;
          if (!hasCurrentVisitors) {
            this.pingCurrentVisitorsForSite();
          }
          return hasCurrentVisitors;
        }),
      );

      // Combine and wait for result
      combineLatest([accountData$, customerData$, limitationsData$, userData$, accountHasVisits$])
        .pipe(
          map(([account, customer, limitations, user, accountHasVisits]) => {
            return <CurrentContext>{
              account: account,
              customer: customer,
              limitations: limitations,
              user: user,
              accountHasVisits: accountHasVisits,
              tierAccess: new TierAccess(account?.tier),
            };
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(
          (currentContext) => {
            const account = currentContext.account;
            const customer = currentContext.customer;
            const limitations = currentContext.limitations;
            this.logger.debug(
              `Setting current context. Account: "${account?.name}" (${account?.id}). Customer: "${customer?.name}" (${customer?.id}). Limitations ID: ${limitations?.id}`,
            );
            this.currentContextBS.next(currentContext);
            this.storageCurrentContextService.createOrUpdate({
              currentCustomerId: customer?.id,
              currentSiteId: account?.siteId,
            });
          },
          (error) => {
            this.logger.error(`Could not get current context. Error: '${JSON.stringify(error)}'`);
          },
        );
    } else {
      this.logger.debug('No SiteId-cookie exists');
    }
  }

  private updateCurrentContext(
    account?: Account,
    customer?: CustomerModel,
    limitations?: Limitations,
    user?: UserModel,
    accountHasVisits?: boolean,
  ) {
    const currentContext = this.currentContext;

    // Copy current context (avoid overwriting any none-existing) and override each property provided in the method
    this.currentContextBS.next({
      ...currentContext,
      account: account || currentContext.account,
      customer: customer || currentContext.customer,
      limitations: limitations || currentContext.limitations,
      user: user || currentContext.user,
      accountHasVisits: accountHasVisits || currentContext.accountHasVisits,
    });
  }

  public updateCurrentContextAccount(account: Account) {
    this.updateCurrentContext(account);
  }

  public updateCurrentContextCustomer(customer: CustomerModel) {
    this.updateCurrentContext(undefined, customer);
  }

  public updateCurrentContextUser(user: UserModel) {
    this.updateCurrentContext(undefined, undefined, undefined, user);
  }

  public updateCurrentContextLimitations(limitations: Limitations) {
    this.updateCurrentContext(undefined, undefined, limitations);
  }

  public patchCurrentContext(partial: Partial<CurrentContext>) {
    const currentContext = this.currentContext;
    this.currentContextBS.next({ ...currentContext, ...partial });
  }

  public updateCurrentContextHasVisits(accountHasVisits: boolean) {
    this.updateCurrentContext(undefined, undefined, undefined, undefined, accountHasVisits);
  }

  public get currentContext() {
    return this.currentContextBS.getValue();
  }

  private getCustomer(account: Account): Observable<CustomerModel> {
    if (account.groupId) {
      return this.customersService.getCustomer(account.groupId);
    }
    this.logger.warn(`Can not get customer. No customer ID for account ${account.id} (${account.name})`);
    return of<CustomerModel>(undefined);
  }

  private getLimitations(account: Account): Observable<Limitations> {
    if (account.groupId) {
      return this.subscriptionService.getLimitations(account.groupId);
    }
    this.logger.warn(`Can not get limitations. No customer ID for account ${account.id} (${account.name})`);
    return of<Limitations>(undefined);
  }

  private getUser(): Promise<UserModel> {
    return this.userService.getUser();
  }
}
